import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { VOICE_TOKEN_ERROR, type EmojiReactionPayload, type VoiceTokenAck, type VoiceHandsUpdatedPayload } from '@deadpan/shared';
import {
  DisconnectReason,
  LocalAudioTrack,
  Room,
  RoomEvent,
  Track,
} from 'livekit-client';
import { useSocket } from '../contexts/SocketContext';
import { useTranslations } from '../hooks/useTranslations';
import { messageForVoiceError } from '../utils/voiceErrors';
import { livekitIdentityIsStale } from '../utils/livekitIdentity';
import { attachRemoteAudioPlayback } from '../utils/remoteAudioPlayback';

const VOICE_DEBUG_PREFIX = '[voice]';
const FLOAT_MARGIN = 8;
const COLLAPSED_SIZE = 56;
/** Bottom-right emoji button + one padding gap when the voice booth snaps to the right. */
const RIGHT_EMOJI_BUTTON_RESERVE = COLLAPSED_SIZE + FLOAT_MARGIN;
const EXPANDED_WIDTH = 320;

function getMaxPanelTop(panelHeight: number, side: SnapSide): number {
  const bottomReserve = side === 'right' ? RIGHT_EMOJI_BUTTON_RESERVE : 0;
  return Math.max( 
    FLOAT_MARGIN,
    window.innerHeight - FLOAT_MARGIN - bottomReserve - panelHeight,
  );
}
const SPEAKING_AUDIO_LEVEL_THRESHOLD = 0.012;
const SPEAKING_UI_HOLD_MS = 180;
const SPEAKING_SYNC_INTERVAL_MS = 120;
const REACTION_BADGE_DURATION_MS = 1800;
const AUTO_JOIN_RETRY_INTERVAL_MS = 4000;
type SnapSide = 'left' | 'right';
type VoiceParticipantItem = {
  id: string;
  displayName: string;
  isSpeaking: boolean;
  isMuted: boolean;
};

type RoomMemberListItem = {
  id: string;
  displayName: string;
  inVoice: boolean;
  isSpeaking: boolean;
  isMuted: boolean;
  handRaised: boolean;
  handRaisedOrder: number | null;
};

type KrispNoiseFilterModule = Awaited<typeof import('@livekit/krisp-noise-filter')>;

function voiceDisplayBaseName(displayName: string, youSuffix: string): string {
  const suffix = ` (${youSuffix})`;
  return displayName.endsWith(suffix) ? displayName.slice(0, -suffix.length) : displayName;
}

async function loadKrispNoiseFilterModule(): Promise<KrispNoiseFilterModule | null> {
  try {
    return await import('@livekit/krisp-noise-filter');
  } catch (error) {
    console.warn('[voice] failed to load Krisp noise filter', error);
    return null;
  }
}

function attachKrispNoiseFilter(
  room: Room,
  gameRoomCode: string,
  krispModule: KrispNoiseFilterModule,
): void {
  room.on(RoomEvent.LocalTrackPublished, async (publication) => {
    if (
      publication.source !== Track.Source.Microphone ||
      !(publication.track instanceof LocalAudioTrack)
    ) {
      return;
    }

    try {
      const processor = krispModule.KrispNoiseFilter();
      await publication.track.setProcessor(processor);
      await processor.setEnabled(true);
      console.log(VOICE_DEBUG_PREFIX, 'Krisp noise filter enabled', { gameRoom: gameRoomCode });
    } catch (error) {
      console.warn('[voice] failed to enable Krisp noise filter', error);
    }
  });
}

/** Optional devtools logging for voice session (no vendor-specific wording). */
function attachVoiceRoomDebugLogs(room: Room, gameRoomCode: string): void {
  const log = (...args: unknown[]) => console.log(VOICE_DEBUG_PREFIX, ...args);

  room.on(RoomEvent.Connected, () => {
    log('local connected', {
      gameRoom: gameRoomCode,
      sessionRoom: room.name,
      localIdentity: room.localParticipant.identity,
      localName: room.localParticipant.name,
      remoteCount: room.remoteParticipants.size,
    });
    room.remoteParticipants.forEach((p) => {
      log('remote already in room', {
        identity: p.identity,
        name: p.name,
        participantSid: p.sid,
      });
    });
  });

  room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
    log('local disconnected', {
      gameRoom: gameRoomCode,
      sessionRoom: room.name,
      reason: reason !== undefined ? DisconnectReason[reason] ?? String(reason) : 'unknown',
    });
  });

  room.on(RoomEvent.ParticipantConnected, (p) => {
    log('remote joined voice room', {
      gameRoom: gameRoomCode,
      sessionRoom: room.name,
      identity: p.identity,
      name: p.name,
      participantSid: p.sid,
    });
  });

  room.on(RoomEvent.ParticipantDisconnected, (p) => {
    log('remote left voice room', {
      gameRoom: gameRoomCode,
      sessionRoom: room.name,
      identity: p.identity,
      name: p.name,
      participantSid: p.sid,
    });
  });

  room.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
    log('local track published', {
      gameRoom: gameRoomCode,
      kind: publication.kind,
      muted: publication.isMuted,
      participantIdentity: participant.identity,
    });
  });

  room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
    if (track.kind === Track.Kind.Audio) {
      log('subscribed to remote audio', {
        gameRoom: gameRoomCode,
        fromIdentity: participant.identity,
        fromName: participant.name,
        trackSid: track.sid,
      });
    }
  });

  room.on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
    if (track.kind === Track.Kind.Audio) {
      log('unsubscribed from remote audio', {
        gameRoom: gameRoomCode,
        fromIdentity: participant.identity,
        fromName: participant.name,
        trackSid: track.sid,
      });
    }
  });

  room.on(RoomEvent.TrackSubscriptionFailed, (trackSid, participant) => {
    log('FAILED to subscribe track', {
      gameRoom: gameRoomCode,
      trackSid,
      participantIdentity: participant.identity,
    });
  });

  room.on(RoomEvent.Reconnecting, () => log('reconnecting…', { gameRoom: gameRoomCode }));
  room.on(RoomEvent.Reconnected, () => log('reconnected', { gameRoom: gameRoomCode }));
}

export const VoiceChatSection: React.FC = () => {
  const { socket, currentRoom, roomMessages, isConnected, myPlayerId } = useSocket();
  const t = useTranslations();
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  /** After host ends voice session for everyone, disable host action until someone joins voice again. */
  const [voiceSessionEndedByHost, setVoiceSessionEndedByHost] = useState(false);
  const [voiceEnabledForRoom, setVoiceEnabledForRoom] = useState(false);
  const [voiceEnableEventVersion, setVoiceEnableEventVersion] = useState(0);
  const roomRef = useRef<Room | null>(null);
  /** Game room code while voice client Room is active (for backend telemetry on disconnect). */
  const voiceSessionGameRoomRef = useRef<string | null>(null);
  const gameRoomPlayersRef = useRef(currentRoom?.players ?? []);
  const [participants, setParticipants] = useState<VoiceParticipantItem[]>([]);
  const [recentReactionsByParticipantId, setRecentReactionsByParticipantId] = useState<
    Record<string, EmojiReactionPayload['emoji']>
  >({});
  const [raisedHandParticipantIds, setRaisedHandParticipantIds] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(true);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [snapSide, setSnapSide] = useState<SnapSide>('left');
  const positionRef = useRef<{ x: number; y: number } | null>(null);
  const participantsSyncTimerRef = useRef<number | null>(null);
  const speakingUntilRef = useRef<Map<string, number>>(new Map());
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const movedDuringDragRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const voiceParticipantListRef = useRef<HTMLUListElement | null>(null);
  const [voiceParticipantListMaxHeightPx, setVoiceParticipantListMaxHeightPx] = useState<number | null>(null);
  const messageFeedRef = useRef<HTMLDivElement | null>(null);
  const [chatDraft, setChatDraft] = useState('');
  const autoJoinAttemptedRef = useRef(false);
  const autoJoinInFlightRef = useRef(false);
  const connectChainRef = useRef(Promise.resolve());
  const reactionClearTimersRef = useRef<Map<string, number>>(new Map());
  /**
   * Tracks the local user's *explicit* mute intent so syncParticipants can read it
   * without needing `muted` in the useCallback dependency array.
   */
  const mutedRef = useRef(false);
  /**
   * Mute-intent overrides keyed by LiveKit identity (player.id).
   * This is the authoritative source for the muted badge in the UI, because
   * LiveKit's publication.isMuted is unreliable in same-machine dual-tab scenarios
   * where the browser's echo cancellation interferes with the underlying MediaStreamTrack.
   */
  const socketMuteOverridesRef = useRef<Map<string, boolean>>(new Map());
  const statusRef = useRef(status);
  const voiceEnabledForRoomRef = useRef(false);
  const voiceSessionEndedByHostRef = useRef(false);
  const isHostRef = useRef(false);

  const requestMicrophonePermissionPreflight = useCallback(async (): Promise<void> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.warn('[voice] microphone preflight failed', error);
    }
  }, []);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    voiceEnabledForRoomRef.current = voiceEnabledForRoom;
  }, [voiceEnabledForRoom]);

  useEffect(() => {
    voiceSessionEndedByHostRef.current = voiceSessionEndedByHost;
  }, [voiceSessionEndedByHost]);

  const clearReactionTimer = useCallback((participantId: string) => {
    const timerId = reactionClearTimersRef.current.get(participantId);
    if (timerId === undefined) return;
    window.clearTimeout(timerId);
    reactionClearTimersRef.current.delete(participantId);
  }, []);

  const clearAllReactionTimers = useCallback(() => {
    reactionClearTimersRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    reactionClearTimersRef.current.clear();
  }, []);

  const stopParticipantSyncLoop = useCallback(() => {
    if (participantsSyncTimerRef.current !== null) {
      window.clearInterval(participantsSyncTimerRef.current);
      participantsSyncTimerRef.current = null;
    }
  }, []);

  const disconnectVoiceSession = useCallback(async () => {
    const r = roomRef.current;
    const gameRoom = voiceSessionGameRoomRef.current;
    roomRef.current = null;
    voiceSessionGameRoomRef.current = null;
    stopParticipantSyncLoop();
    speakingUntilRef.current.clear();
    socketMuteOverridesRef.current.clear();
    if (gameRoom && socket?.connected) {
      socket.emit('client:voice-session-disconnected', { roomCode: gameRoom });
    }
    if (r) {
      await r.disconnect();
    }
    setParticipants([]);
  }, [socket, stopParticipantSyncLoop]);

  useEffect(() => {
    return () => {
      clearAllReactionTimers();
      void disconnectVoiceSession();
    };
  }, [clearAllReactionTimers, disconnectVoiceSession]);

  useEffect(() => {
    if (!socket) return;
    const onVoiceRoomEnabled = (): void => {
      setVoiceSessionEndedByHost(false);
      setVoiceEnabledForRoom(true);
      autoJoinAttemptedRef.current = false;
      setVoiceEnableEventVersion((prev) => prev + 1);
    };
    const onVoiceRoomClosed = (): void => {
      setVoiceSessionEndedByHost(true);
      setVoiceEnabledForRoom(false);
      autoJoinAttemptedRef.current = true;
      void disconnectVoiceSession();
      setStatus('idle');
      setMuted(false);
    };
    socket.on('server:voice-room-enabled', onVoiceRoomEnabled);
    socket.on('server:voice-room-closed', onVoiceRoomClosed);
    return () => {
      socket.off('server:voice-room-enabled', onVoiceRoomEnabled);
      socket.off('server:voice-room-closed', onVoiceRoomClosed);
    };
  }, [socket, disconnectVoiceSession]);

  useEffect(() => {
    setVoiceSessionEndedByHost(false);
    setVoiceEnabledForRoom(false);
    autoJoinAttemptedRef.current = false;
    clearAllReactionTimers();
    setRecentReactionsByParticipantId({});
    setRaisedHandParticipantIds([]);
    setChatDraft('');
    const initial = {
      x: FLOAT_MARGIN,
      y: getMaxPanelTop(COLLAPSED_SIZE, 'left'),
    };
    setPosition(initial);
    setSnapSide('left');
    positionRef.current = initial;
    setExpanded(true);
  }, [clearAllReactionTimers, currentRoom?.code]);

  useEffect(() => {
    if (!socket || !currentRoom) return;
    const onEmojiReaction = (payload: { emoji: EmojiReactionPayload['emoji']; senderId: string }): void => {
      const senderId = typeof payload?.senderId === 'string' ? payload.senderId : '';
      const emoji = payload?.emoji;
      if (!senderId || !emoji) return;
      clearReactionTimer(senderId);
      setRecentReactionsByParticipantId((prev) => ({ ...prev, [senderId]: emoji }));
      const timerId = window.setTimeout(() => {
        setRecentReactionsByParticipantId((prev) => {
          if (!prev[senderId]) return prev;
          const next = { ...prev };
          delete next[senderId];
          return next;
        });
        reactionClearTimersRef.current.delete(senderId);
      }, REACTION_BADGE_DURATION_MS);
      reactionClearTimersRef.current.set(senderId, timerId);
    };
    socket.on('server:emoji-reaction', onEmojiReaction);
    return () => {
      socket.off('server:emoji-reaction', onEmojiReaction);
    };
  }, [clearReactionTimer, currentRoom, socket]);

  useEffect(() => {
    if (!socket || !currentRoom) return;
    const onVoiceHandsUpdated = (payload: VoiceHandsUpdatedPayload): void => {
      const ids = Array.isArray(payload?.participantIds)
        ? payload.participantIds.filter((id): id is string => typeof id === 'string')
        : [];
      setRaisedHandParticipantIds(ids);
    };
    socket.on('server:voice-hands-updated', onVoiceHandsUpdated);
    return () => {
      socket.off('server:voice-hands-updated', onVoiceHandsUpdated);
    };
  }, [currentRoom, socket]);

  const toggleRaiseHand = useCallback(() => {
    if (!socket || !myPlayerId || !currentRoom) return;
    const isRaised = raisedHandParticipantIds.includes(myPlayerId);
    socket.emit('client:set-voice-hand-raised', {
      roomCode: currentRoom.code,
      raised: !isRaised,
    });
  }, [currentRoom, myPlayerId, raisedHandParticipantIds, socket]);

  const sendChatMessage = useCallback(() => {
    if (!socket?.id || !currentRoom) return;
    const content = chatDraft.trim();
    if (!content) return;
    socket.emit('client:send-room-message', {
      roomCode: currentRoom.code,
      content,
    });
    setChatDraft('');
  }, [chatDraft, currentRoom, socket]);

  const syncParticipants = useCallback((room: Room) => {
    const now = Date.now();
    const activeSpeakerIds = new Set(
      room.activeSpeakers.map((participant) => participant.identity || participant.sid),
    );
    const nextParticipants: VoiceParticipantItem[] = [];
    const speakingUntil = speakingUntilRef.current;
    const currentPlayers = gameRoomPlayersRef.current;
    const playerNamesById = new Map(currentPlayers.map((player) => [player.id, player.name]));

    const resolveSpeaking = (id: string, audioLevel: number): boolean => {
      const speakingNow = activeSpeakerIds.has(id) || audioLevel >= SPEAKING_AUDIO_LEVEL_THRESHOLD;
      if (speakingNow) {
        speakingUntil.set(id, now + SPEAKING_UI_HOLD_MS);
        return true;
      }
      const expiresAt = speakingUntil.get(id) ?? 0;
      if (expiresAt > now) {
        return true;
      }
      speakingUntil.delete(id);
      return false;
    };

    const localId = room.localParticipant.identity || room.localParticipant.sid;
    const localName =
      playerNamesById.get(localId) ||
      room.localParticipant.name ||
      room.localParticipant.identity ||
      t.ui.you;
    nextParticipants.push({
      id: localId,
      displayName: `${localName} (${t.ui.voiceParticipantYouSuffix})`,
      isSpeaking: resolveSpeaking(localId, room.localParticipant.audioLevel),
      // Use the explicit intent ref — never derive local mute from the publication,
      // which can be corrupted by echo-cancellation in same-machine scenarios.
      isMuted: mutedRef.current,
    });

    room.remoteParticipants.forEach((participant) => {
      const participantId = participant.identity || participant.sid;
      const micPublication = participant.getTrackPublication(Track.Source.Microphone);
      // Socket-based override is the authoritative source (set via server:voice-mute-changed).
      // Fall back to LiveKit publication state only when no explicit signal has arrived yet.
      const socketOverride = socketMuteOverridesRef.current.get(participantId);
      const isMuted = socketOverride !== undefined ? socketOverride : (micPublication?.isMuted ?? true);
      nextParticipants.push({
        id: participantId,
        displayName:
          playerNamesById.get(participantId) ||
          participant.name ||
          participant.identity ||
          t.ui.voiceUnknownParticipant,
        isSpeaking: resolveSpeaking(participantId, participant.audioLevel),
        isMuted,
      });
    });

    nextParticipants.sort((a, b) => a.displayName.localeCompare(b.displayName));
    setParticipants(nextParticipants);
  }, [t.ui.voiceParticipantYouSuffix, t.ui.voiceUnknownParticipant, t.ui.you]);

  useEffect(() => {
    gameRoomPlayersRef.current = currentRoom?.players ?? [];
    if (roomRef.current) {
      syncParticipants(roomRef.current);
    }
  }, [currentRoom?.players, syncParticipants]);

  useEffect(() => {
    if (!socket) return;
    const onVoiceMuteChanged = (payload: { participantId: string; muted: boolean }): void => {
      const id = typeof payload?.participantId === 'string' ? payload.participantId : '';
      if (!id) return;
      socketMuteOverridesRef.current.set(id, Boolean(payload.muted));
      if (roomRef.current) {
        syncParticipants(roomRef.current);
      }
    };
    socket.on('server:voice-mute-changed', onVoiceMuteChanged);
    return () => {
      socket.off('server:voice-mute-changed', onVoiceMuteChanged);
    };
  }, [socket, syncParticipants]);

  const connectWithToken = useCallback(
    async (url: string, token: string) => {
      if (!currentRoom) return;
      const run = async () => {
      try {
        await disconnectVoiceSession();
        const krispModule = await loadKrispNoiseFilterModule();
        const useKrisp = Boolean(krispModule?.isKrispNoiseFilterSupported());
        const room = new Room({
          adaptiveStream: true,
          dynacast: true,
          audioCaptureDefaults: {
            noiseSuppression: !useKrisp,
            echoCancellation: true,
            autoGainControl: true,
          },
        });
        roomRef.current = room;
        stopParticipantSyncLoop();
        attachVoiceRoomDebugLogs(room, currentRoom.code);
        const cleanupRemoteAudioPlayback = attachRemoteAudioPlayback(room);
        if (useKrisp && krispModule) {
          attachKrispNoiseFilter(room, currentRoom.code, krispModule);
        }
        room.on(RoomEvent.Disconnected, () => {
          cleanupRemoteAudioPlayback();
          stopParticipantSyncLoop();
          speakingUntilRef.current.clear();
          voiceSessionGameRoomRef.current = null;
          if (roomRef.current === room) {
            roomRef.current = null;
            setParticipants([]);
            // Keep mute intent: unexpected drops should auto-rejoin with the same mic state.
            // Host-ended sessions reset mute in the voice-room-closed handler.
            if (!voiceSessionEndedByHostRef.current) {
              autoJoinAttemptedRef.current = false;
              setStatus('idle');
            }
          }
        });
        room.on(RoomEvent.ParticipantConnected, () => syncParticipants(room));
        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
          // Remove the disconnected participant's socket mute override to avoid stale state
          // if they rejoin later with a different actual mute state.
          const id = participant.identity || participant.sid;
          socketMuteOverridesRef.current.delete(id);
          syncParticipants(room);
        });
        room.on(RoomEvent.ActiveSpeakersChanged, () => syncParticipants(room));
        // Secondary triggers: let LiveKit events also refresh the list, even though the
        // socket-based override is now the primary source of truth for isMuted.
        room.on(RoomEvent.TrackMuted, () => syncParticipants(room));
        room.on(RoomEvent.TrackUnmuted, () => syncParticipants(room));
        await room.connect(url, token);
        syncParticipants(room);
        participantsSyncTimerRef.current = window.setInterval(() => {
          syncParticipants(room);
        }, SPEAKING_SYNC_INTERVAL_MS);
        voiceSessionGameRoomRef.current = currentRoom.code;
        if (socket?.connected) {
          socket.emit('client:voice-session-connected', { roomCode: currentRoom.code });
        }
        const restoreMuted = mutedRef.current;
        let micEnabled = false;
        try {
          await room.localParticipant.setMicrophoneEnabled(!restoreMuted);
          micEnabled = !restoreMuted;
          if (micEnabled && socket?.connected) {
            socket.emit('client:voice-mic-enabled', { roomCode: currentRoom.code });
          }
        } catch (micErr) {
          console.warn('[voice] microphone permission rejected or unavailable', micErr);
        }
        const micPublication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
        console.log(VOICE_DEBUG_PREFIX, 'local mic after setMicrophoneEnabled', {
          gameRoom: currentRoom.code,
          identity: room.localParticipant.identity,
          trackExists: Boolean(micPublication?.track),
          published: Boolean(micPublication),
          muted: micPublication?.isMuted ?? null,
        });
        try {
          await room.startAudio();
        } catch {
          // Autoplay policies: playback may need another user gesture; remote attach still runs.
        }
        const initialMuted = !micEnabled;
        // Broadcast initial mute state so all peers start with consistent display.
        mutedRef.current = initialMuted;
        socketMuteOverridesRef.current.set(
          room.localParticipant.identity || room.localParticipant.sid,
          initialMuted,
        );
        if (socket?.connected) {
          socket.emit('client:voice-mute-changed', { roomCode: currentRoom.code, muted: initialMuted });
        }
        setMuted(initialMuted);
        setVoiceSessionEndedByHost(false);
        setVoiceEnabledForRoom(true);
        setStatus('connected');
      } catch (e) {
        console.error(e);
        await disconnectVoiceSession();
        setStatus('error');
        setError(t.ui.voiceErrInternal);
      }
      };
      const next = connectChainRef.current.then(run, run);
      connectChainRef.current = next.then(
        () => undefined,
        () => undefined,
      );
      await next;
    },
    [currentRoom, disconnectVoiceSession, socket, stopParticipantSyncLoop, syncParticipants, t.ui.voiceErrInternal],
  );

  const isHost = Boolean(socket && currentRoom && currentRoom.hostId === myPlayerId);
  isHostRef.current = isHost;
  const hostEndVoiceDisabled = voiceSessionEndedByHost && status !== 'connected';
  const canHostReopenWithoutPassword = isHost && voiceSessionEndedByHost && !voiceEnabledForRoom;
  const roomMemberCount = currentRoom?.players.length ?? 0;

  const requestVoiceToken = useCallback(
    async (voicePassword?: string): Promise<VoiceTokenAck | null> => {
      if (!socket || !currentRoom || !myPlayerId) return null;
      return await new Promise<VoiceTokenAck>((resolve) => {
        socket.emit(
          'client:request-voice-token',
          {
            roomCode: currentRoom.code,
            ...(voicePassword ? { voicePassword } : {}),
          },
          (ack: VoiceTokenAck) => resolve(ack),
        );
      });
    },
    [socket, currentRoom, myPlayerId],
  );

  const voiceIdentityIsStale = useCallback((): boolean => {
    return livekitIdentityIsStale(roomRef.current?.localParticipant.identity, myPlayerId);
  }, [myPlayerId]);

  const attemptAutoJoin = useCallback(async (options?: { silent?: boolean; force?: boolean }) => {
    const silent = options?.silent ?? false;
    const force = options?.force ?? false;
    if (!currentRoom || !socket || !myPlayerId) return;
    if (voiceSessionEndedByHostRef.current) return;
    // Host must click Join + password the first time. After voice is on, they may auto-rejoin.
    if (isHostRef.current && !voiceEnabledForRoomRef.current) return;
    if (autoJoinInFlightRef.current) return;
    if (statusRef.current === 'connecting' && !force) return;
    if (statusRef.current === 'connected' && !force && !voiceIdentityIsStale()) {
      return;
    }
    autoJoinInFlightRef.current = true;
    try {
      const showConnecting = !silent || voiceEnabledForRoomRef.current;
      if (showConnecting && statusRef.current !== 'connected') {
        setError(null);
        setStatus('connecting');
      }
      const ack = await requestVoiceToken();
      if (!ack) {
        if (!silent && statusRef.current !== 'connected') {
          setStatus('idle');
        }
        return;
      }
      if (!ack.ok) {
        if (ack.code === VOICE_TOKEN_ERROR.NOT_IN_GAME_ROOM) {
          autoJoinAttemptedRef.current = false;
          if (statusRef.current !== 'connected') {
            setStatus('idle');
          }
          return;
        }
        if (ack.code === VOICE_TOKEN_ERROR.VOICE_NOT_SET) {
          if (!silent) {
            setStatus('idle');
          }
          return;
        }
        if (!silent) {
          setStatus('error');
          setError(messageForVoiceError(ack.code, t.ui));
        }
        return;
      }
      setError(null);
      setStatus('connecting');
      await connectWithToken(ack.url, ack.token);
    } finally {
      autoJoinInFlightRef.current = false;
    }
  }, [connectWithToken, currentRoom, myPlayerId, requestVoiceToken, socket, t.ui, voiceIdentityIsStale]);

  const attemptAutoJoinRef = useRef(attemptAutoJoin);
  attemptAutoJoinRef.current = attemptAutoJoin;

  useEffect(() => {
    if (!isConnected || !voiceEnabledForRoom || voiceSessionEndedByHost) return;
    autoJoinAttemptedRef.current = false;
  }, [isConnected, myPlayerId, voiceEnabledForRoom, voiceSessionEndedByHost]);

  useEffect(() => {
    if (!socket || !currentRoom || !myPlayerId) return;
    if (voiceSessionEndedByHost) return;
    if (status === 'connected' && !voiceIdentityIsStale()) return;
    if (isHost && !voiceEnabledForRoom) return;
    if (!isConnected) return;

    if (!autoJoinAttemptedRef.current) {
      autoJoinAttemptedRef.current = true;
      void attemptAutoJoinRef.current({ silent: voiceEnabledForRoom, force: voiceIdentityIsStale() });
    }

    const retryId = window.setInterval(() => {
      if (statusRef.current === 'connected' && !voiceIdentityIsStale()) return;
      void attemptAutoJoinRef.current({ silent: true, force: false });
    }, AUTO_JOIN_RETRY_INTERVAL_MS);
    return () => {
      window.clearInterval(retryId);
    };
  }, [
    currentRoom?.code,
    isConnected,
    isHost,
    myPlayerId,
    socket,
    status,
    voiceEnabledForRoom,
    voiceEnableEventVersion,
    voiceIdentityIsStale,
    voiceSessionEndedByHost,
  ]);

  useEffect(() => {
    if (status !== 'connected') return;
    const resumeAudio = (): void => {
      void roomRef.current?.startAudio();
    };
    document.addEventListener('click', resumeAudio);
    document.addEventListener('touchstart', resumeAudio);
    return () => {
      document.removeEventListener('click', resumeAudio);
      document.removeEventListener('touchstart', resumeAudio);
    };
  }, [status]);

  const handleJoin = () => {
    if (!socket || !currentRoom || status === 'connecting') return;
    if (!voiceEnabledForRoom && !password.trim() && !canHostReopenWithoutPassword) return;
    if (autoJoinInFlightRef.current) return;
    setError(null);
    setStatus('connecting');
    autoJoinInFlightRef.current = true;
    void (async () => {
      try {
        if (isHost) {
          await requestMicrophonePermissionPreflight();
        }
        const passwordToUse = canHostReopenWithoutPassword ? undefined : password.trim();
        const ack = await requestVoiceToken(passwordToUse);
        if (!ack) {
          setStatus('idle');
          return;
        }
        if (!ack.ok) {
          if (ack.code === VOICE_TOKEN_ERROR.NOT_IN_GAME_ROOM) {
            autoJoinAttemptedRef.current = false;
            setStatus('idle');
            return;
          }
          setStatus('error');
          setError(messageForVoiceError(ack.code, t.ui));
          return;
        }
        await connectWithToken(ack.url, ack.token);
      } finally {
        autoJoinInFlightRef.current = false;
      }
    })();
  };

  const toggleMute = async () => {
    const r = roomRef.current;
    if (!r || status !== 'connected') return;
    const nextMuted = !muted;
    try {
      await r.localParticipant.setMicrophoneEnabled(!nextMuted);
    } catch (err) {
      console.warn('[voice] failed to toggle microphone', err);
      return;
    }
    if (!nextMuted && socket?.connected && currentRoom) {
      socket.emit('client:voice-mic-enabled', { roomCode: currentRoom.code });
    }
    // Update the ref immediately so syncParticipants sees the new intent before React re-renders.
    mutedRef.current = nextMuted;
    // Record local participant's override so our own entry in the list is correct instantly.
    const localId = r.localParticipant.identity || r.localParticipant.sid;
    socketMuteOverridesRef.current.set(localId, nextMuted);
    // Broadcast explicit mute intent to all peers via socket (bypasses LiveKit's unreliable
    // mute signaling in same-machine dual-tab + echo-cancellation scenarios).
    if (socket?.connected && currentRoom) {
      socket.emit('client:voice-mute-changed', { roomCode: currentRoom.code, muted: nextMuted });
    }
    setMuted(nextMuted);
    // Force immediate participant list refresh so the local tab's own badge is correct.
    syncParticipants(r);
  };

  if (!currentRoom) {
    return null;
  }

  const clampToViewport = useCallback((x: number, y: number, width: number, height: number, side: SnapSide) => {
    const clampedX = Math.min(Math.max(FLOAT_MARGIN, x), Math.max(FLOAT_MARGIN, window.innerWidth - width - FLOAT_MARGIN));
    const clampedY = Math.min(Math.max(FLOAT_MARGIN, y), getMaxPanelTop(height, side));
    return { x: clampedX, y: clampedY };
  }, []);

  const snapToSide = useCallback(
    (x: number, y: number, width: number, height: number, preferredSide?: SnapSide) => {
      const leftDist = Math.abs(x - FLOAT_MARGIN);
      const rightX = Math.max(FLOAT_MARGIN, window.innerWidth - width - FLOAT_MARGIN);
      const rightDist = Math.abs(x - rightX);
      const side: SnapSide = preferredSide ?? (leftDist <= rightDist ? 'left' : 'right');
      const snapX = side === 'left' ? FLOAT_MARGIN : rightX;
      const clamped = clampToViewport(snapX, y, width, height, side);
      return { position: { x: snapX, y: clamped.y }, side };
    },
    [clampToViewport],
  );

  const detectCurrentSide = (): SnapSide => {
    const panel = panelRef.current;
    if (panel && positionRef.current) {
      const width = panel.getBoundingClientRect().width || panel.offsetWidth || COLLAPSED_SIZE;
      const rightX = Math.max(FLOAT_MARGIN, window.innerWidth - width - FLOAT_MARGIN);
      const leftDist = Math.abs(positionRef.current.x - FLOAT_MARGIN);
      const rightDist = Math.abs(positionRef.current.x - rightX);
      return leftDist <= rightDist ? 'left' : 'right';
    }
    return 'left';
  };

  const startDrag = (event: React.MouseEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    dragStartRef.current = { x: event.clientX, y: event.clientY };
    draggingRef.current = true;
    movedDuringDragRef.current = false;
    if (!position) {
      const next = { x: rect.left, y: rect.top };
      positionRef.current = next;
      setPosition(next);
    }
  };

  const alignForExpandedState = (nextExpanded: boolean) => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const basePos = positionRef.current ?? { x: rect.left, y: rect.top };
    const width = nextExpanded
      ? Math.min(EXPANDED_WIDTH, Math.max(COLLAPSED_SIZE, window.innerWidth - FLOAT_MARGIN * 2))
      : COLLAPSED_SIZE;
    const height = nextExpanded ? panel.offsetHeight : COLLAPSED_SIZE;
    const side = detectCurrentSide();
    const snapped = snapToSide(basePos.x, basePos.y, width, height, side);
    positionRef.current = snapped.position;
    setPosition(snapped.position);
    setSnapSide(snapped.side);
  };

  const toggleExpanded = (nextExpanded: boolean) => {
    alignForExpandedState(nextExpanded);
    setExpanded(nextExpanded);
  };

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      if (!draggingRef.current || !dragOffsetRef.current || !panelRef.current) return;
      if (dragStartRef.current) {
        const deltaX = Math.abs(event.clientX - dragStartRef.current.x);
        const deltaY = Math.abs(event.clientY - dragStartRef.current.y);
        if (deltaX > 3 || deltaY > 3) {
          movedDuringDragRef.current = true;
          if (!isDragging) {
            setIsDragging(true);
          }
        }
      }
      const width = panelRef.current.offsetWidth;
      const height = panelRef.current.offsetHeight;
      const nextX = event.clientX - dragOffsetRef.current.x;
      const nextY = event.clientY - dragOffsetRef.current.y;
      const next = clampToViewport(nextX, nextY, width, height, detectCurrentSide());
      positionRef.current = next;
      setPosition(next);
    };

    const onMouseUp = () => {
      if (draggingRef.current && movedDuringDragRef.current && panelRef.current && positionRef.current) {
        const width = panelRef.current.offsetWidth;
        const height = panelRef.current.offsetHeight;
        const snapped = snapToSide(positionRef.current.x, positionRef.current.y, width, height);
        positionRef.current = snapped.position;
        setPosition(snapped.position);
        setSnapSide(snapped.side);
      }
      draggingRef.current = false;
      setIsDragging(false);
      movedDuringDragRef.current = false;
      dragStartRef.current = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [clampToViewport, snapToSide]);

  useEffect(() => {
    const onResize = () => {
      if (!panelRef.current || !positionRef.current) return;
      const width = panelRef.current.offsetWidth;
      const height = panelRef.current.offsetHeight;
      const side = detectCurrentSide();
      const snapped = snapToSide(positionRef.current.x, positionRef.current.y, width, height, side);
      positionRef.current = snapped.position;
      setPosition(snapped.position);
      setSnapSide(snapped.side);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [snapToSide]);

  useEffect(() => {
    if (!panelRef.current || !positionRef.current) return;
    const width = panelRef.current.offsetWidth;
    const height = panelRef.current.offsetHeight;
    // Recalculate with current panel size when toggling expanded/collapsed,
    // or when participant list height changes, so the panel stays in viewport.
    const side = detectCurrentSide();
    const snapped = snapToSide(positionRef.current.x, positionRef.current.y, width, height, side);
    positionRef.current = snapped.position;
    setPosition(snapped.position);
    setSnapSide(snapped.side);
  }, [expanded, participants.length, roomMemberCount, snapToSide]);

  useLayoutEffect(() => {
    if (!expanded) {
      setVoiceParticipantListMaxHeightPx(null);
      return;
    }
    if (isDragging) {
      return;
    }
    const panel = panelRef.current;
    const ul = voiceParticipantListRef.current;
    if (!panel || !positionRef.current) {
      setVoiceParticipantListMaxHeightPx(null);
      return;
    }
    const applyVerticalFit = (): void => {
      const p = panelRef.current;
      if (!p || !positionRef.current) return;

      const side = detectCurrentSide();
      const bottomReserve = side === 'right' ? RIGHT_EMOJI_BUTTON_RESERVE : 0;
      const vh = window.innerHeight;
      const available = vh - FLOAT_MARGIN * 2 - bottomReserve;

      if (roomMemberCount === 0 || !ul) {
        setVoiceParticipantListMaxHeightPx(null);
        const panelH = p.getBoundingClientRect().height;
        const maxTop = getMaxPanelTop(panelH, side);
        const nextY = Math.max(FLOAT_MARGIN, Math.min(positionRef.current.y, maxTop));
        if (nextY !== positionRef.current.y) {
          const next = { ...positionRef.current, y: nextY };
          positionRef.current = next;
          setPosition(next);
        }
        return;
      }

      const listEl = ul;
      const panelRect = p.getBoundingClientRect();
      const ulRect = listEl.getBoundingClientRect();
      const chromeAbove = ulRect.top - panelRect.top;
      const chromeBelow = panelRect.bottom - ulRect.bottom;
      const listNatural = listEl.scrollHeight;
      const totalNatural = chromeAbove + listNatural + chromeBelow;

      if (totalNatural <= available) {
        setVoiceParticipantListMaxHeightPx(null);
        const panelH = panelRect.height;
        const maxTop = getMaxPanelTop(panelH, side);
        const nextY = Math.max(FLOAT_MARGIN, Math.min(positionRef.current.y, maxTop));
        if (nextY !== positionRef.current.y) {
          const next = { ...positionRef.current, y: nextY };
          positionRef.current = next;
          setPosition(next);
        }
        return;
      }

      const maxListH = Math.max(64, Math.floor(available - chromeAbove - chromeBelow));
      setVoiceParticipantListMaxHeightPx(maxListH);
      const shrunkPanelH = chromeAbove + maxListH + chromeBelow;
      const maxTop = getMaxPanelTop(shrunkPanelH, side);
      const nextY = Math.max(FLOAT_MARGIN, Math.min(positionRef.current.y, maxTop));
      if (nextY !== positionRef.current.y) {
        const next = { ...positionRef.current, y: nextY };
        positionRef.current = next;
        setPosition(next);
      }
    };

    applyVerticalFit();
    window.addEventListener('resize', applyVerticalFit);
    const ro = new ResizeObserver(applyVerticalFit);
    ro.observe(panel);
    return () => {
      window.removeEventListener('resize', applyVerticalFit);
      ro.disconnect();
    };
  }, [expanded, participants.length, roomMemberCount, isHost, status, position, isDragging]);

  useEffect(() => {
    const el = messageFeedRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [roomMessages.length]);

  const anchoredStyle = position
    ? isDragging
      ? { left: position.x, top: position.y }
      : snapSide === 'right'
      ? { right: FLOAT_MARGIN, top: position.y }
      : { left: position.x, top: position.y }
    : { left: FLOAT_MARGIN, top: FLOAT_MARGIN };
  const voiceParticipantsById = new Map(participants.map((participant) => [participant.id, participant]));
  const youSuffix = t.ui.voiceParticipantYouSuffix;
  const voiceParticipantsByName = new Map(
    participants.map((participant) => [voiceDisplayBaseName(participant.displayName, youSuffix), participant]),
  );
  const raisedHandOrderById = new Map(raisedHandParticipantIds.map((id, index) => [id, index]));
  const roomMembers: RoomMemberListItem[] = [...currentRoom.players]
    .map((player) => {
      const voiceParticipant = voiceParticipantsById.get(player.id) ?? voiceParticipantsByName.get(player.name);
      const isSelf = player.id === myPlayerId;
      const handRaisedOrder = raisedHandOrderById.get(player.id) ?? null;
      return {
        id: player.id,
        displayName: isSelf ? `${player.name} (${t.ui.voiceParticipantYouSuffix})` : player.name,
        inVoice: Boolean(voiceParticipant),
        isSpeaking: voiceParticipant?.isSpeaking ?? false,
        isMuted: voiceParticipant?.isMuted ?? true,
        handRaised: handRaisedOrder !== null,
        handRaisedOrder,
      };
    })
    .sort((a, b) => {
      if (a.handRaisedOrder !== null && b.handRaisedOrder !== null) {
        return a.handRaisedOrder - b.handRaisedOrder;
      }
      if (a.handRaisedOrder !== null) return -1;
      if (b.handRaisedOrder !== null) return 1;
      return a.displayName.localeCompare(b.displayName);
    });

  return (
    <div
      ref={panelRef}
      className={`fixed z-[100] max-w-[calc(100vw-1rem)] ${
        isDragging ? '' : 'transition-[width,height,left,right,top] duration-200'
      } ${
        expanded ? 'w-[320px]' : 'w-14 h-14'
      }`}
      style={anchoredStyle}
    >
      {!expanded ? (
        <button
          type="button"
          onMouseDown={startDrag}
          onClick={() => {
            if (movedDuringDragRef.current) {
              movedDuringDragRef.current = false;
              return;
            }
            toggleExpanded(true);
          }}
          aria-label={t.ui.voiceTitle}
          className="relative h-14 w-14 rounded-full bg-teal-100 border-2 border-teal-300 shadow-xl flex items-center justify-center cursor-move select-none hover:bg-teal-200"
        >
          <span className="text-xl leading-none" aria-hidden>
            🥳
          </span>
          <span
            className={`absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full ${
              status === 'connected' ? 'bg-emerald-500' : 'bg-gray-400'
            }`}
          />
        </button>
      ) : (
      <div className="bg-teal-50 border-2 border-teal-200 rounded-xl shadow-xl overflow-hidden">
        <div
          className="px-3 py-2 bg-teal-100 border-b border-teal-200 flex items-center justify-between cursor-move select-none"
          onMouseDown={startDrag}
        >
          <button
            type="button"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={() => toggleExpanded(!expanded)}
            className="font-semibold text-teal-900 text-sm flex items-center gap-2 hover:text-teal-700"
          >
            <span aria-hidden>🥳</span>
            <span>{t.ui.voiceTitle}</span>
            <span className={`h-2.5 w-2.5 rounded-full ${status === 'connected' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          </button>
          <button
            type="button"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={() => toggleExpanded(!expanded)}
            className="text-teal-900/80 hover:text-teal-900 text-xs font-semibold"
          >
            {expanded ? t.ui.voicePanelHide : t.ui.voicePanelShow}
          </button>
        </div>

        {expanded && (
          <div className="p-3">
            {(isHost || voiceEnabledForRoom) && (
              <p className="text-xs text-teal-800/90 mb-3">
                {isHost
                  ? canHostReopenWithoutPassword
                    ? t.ui.voiceReopenHint
                    : t.ui.voiceHint
                  : t.ui.voiceWaitingForHost}
              </p>
            )}
            {status !== 'connected' ? (
              isHost ? (
                <div className="space-y-2">
                  {!voiceEnabledForRoom && !canHostReopenWithoutPassword && (
                    <>
                      <label className="block text-xs font-semibold text-teal-900">{t.ui.voicePasswordLabel}</label>
                      <input
                        type="password"
                        autoComplete="off"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t.ui.voicePasswordPlaceholder}
                        className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm bg-white"
                        maxLength={32}
                      />
                    </>
                  )}
                  <button
                    type="button"
                    onClick={handleJoin}
                    disabled={
                      !socket ||
                      status === 'connecting' ||
                      (!voiceEnabledForRoom && !password.trim() && !canHostReopenWithoutPassword)
                    }
                    className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {status === 'connecting'
                      ? t.ui.voiceConnecting
                      : canHostReopenWithoutPassword
                      ? t.ui.voiceReopen
                      : t.ui.voiceJoin}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {status === 'connecting' && (
                    <p className="text-sm font-medium text-teal-800">{t.ui.voiceConnecting}</p>
                  )}
                </div>
              )
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-teal-800">{t.ui.voiceConnected}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void toggleMute()}
                    className="px-3 py-1.5 bg-teal-100 text-teal-900 rounded-lg text-sm font-semibold hover:bg-teal-200"
                  >
                    {muted ? t.ui.voiceUnmute : t.ui.voiceMute}
                  </button>
                </div>
              </div>
            )}

            {isHost && (
              <div className="mt-3 pt-2 border-t border-teal-200">
                <button
                  type="button"
                  disabled={!socket || hostEndVoiceDisabled}
                  onClick={() => {
                    if (!socket || hostEndVoiceDisabled) return;
                    socket.emit('client:close-voice-room');
                  }}
                  className="px-3 py-1.5 bg-amber-100 text-amber-950 rounded-lg text-xs font-semibold hover:bg-amber-200 border border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-amber-100"
                >
                  {t.ui.voiceHostEndSession}
                </button>
              </div>
            )}

            <div className={isHost ? 'mt-3 pt-2 border-t border-teal-200' : 'mt-0'}>
              <p className="text-xs font-semibold text-teal-900 mb-1">{t.ui.voiceParticipantsTitle}</p>
              {roomMembers.length > 0 ? (
                <ul
                  ref={voiceParticipantListRef}
                  className="space-y-1 overflow-y-auto"
                  style={
                    voiceParticipantListMaxHeightPx != null
                      ? { maxHeight: voiceParticipantListMaxHeightPx }
                      : undefined
                  }
                >
                  {roomMembers.map((participant) => (
                    <li
                      key={participant.id}
                      className={`text-xs px-2 py-1 rounded-md flex items-center gap-2 transition-all duration-300 ease-in-out ${
                        participant.handRaised
                          ? 'bg-amber-100/80 text-amber-950 ring-1 ring-amber-300/80'
                          : participant.isSpeaking
                          ? 'bg-emerald-100/70 text-emerald-900'
                          : 'bg-teal-100/60 text-teal-800'
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`h-6 w-6 shrink-0 rounded-full border-2 flex items-center justify-center text-[10px] font-bold leading-none transition-all ${
                          participant.isSpeaking
                            ? 'border-emerald-400 bg-white text-emerald-700 shadow-[0_0_0_3px_rgba(16,185,129,0.25)] animate-pulse'
                            : 'border-teal-300 bg-white text-teal-700'
                        }`}
                      >
                        {participant.displayName.trim().charAt(0).toUpperCase()}
                      </span>
                      <span
                        className={`min-w-0 flex-1 truncate ${
                          participant.isSpeaking ? 'font-semibold' : ''
                        }`}
                      >
                        {participant.displayName}
                      </span>
                      {participant.inVoice ? (
                        <span className="text-base leading-none shrink-0" aria-label="in voice">
                          🎙️
                        </span>
                      ) : null}
                      {participant.handRaised ? (
                        <span
                          className="text-base leading-none shrink-0"
                          aria-label={t.ui.voiceHandRaisedLabel}
                          title={t.ui.voiceHandRaisedLabel}
                        >
                          ✋
                        </span>
                      ) : null}
                      {recentReactionsByParticipantId[participant.id] ? (
                        <span
                          className="text-base leading-none shrink-0"
                          aria-label={`reaction ${recentReactionsByParticipantId[participant.id]}`}
                        >
                          {recentReactionsByParticipantId[participant.id]}
                        </span>
                      ) : null}
                      <div className="ml-auto flex items-center gap-1 shrink-0">
                        {participant.id === myPlayerId ? (
                          <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={toggleRaiseHand}
                            className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border transition-colors ${
                              participant.handRaised
                                ? 'bg-amber-200 text-amber-950 border-amber-300 hover:bg-amber-300'
                                : 'bg-white text-teal-800 border-teal-200 hover:bg-teal-50'
                            }`}
                            aria-pressed={participant.handRaised}
                          >
                            {participant.handRaised ? t.ui.voiceLowerHand : t.ui.voiceRaiseHand}
                          </button>
                        ) : null}
                        {participant.inVoice && participant.isMuted ? (
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wide text-amber-900 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"
                            title={t.ui.voiceMute}
                            aria-label={t.ui.voiceMute}
                          >
                            muted
                          </span>
                        ) : null}
                        {isHost && participant.id !== myPlayerId ? (
                          <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => {
                              if (window.confirm(t.ui.removePlayerConfirm)) {
                                socket?.emit('client:kick-player', participant.id);
                              }
                            }}
                            className="text-xs text-red-500 hover:text-red-700 opacity-70 hover:opacity-100 transition-opacity px-1 py-0.5 rounded hover:bg-red-50"
                            title={t.ui.removePlayerTooltip}
                          >
                            🚫
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-teal-700">{t.ui.voiceParticipantsEmpty}</p>
              )}
            </div>

            {error && status === 'error' && <p className="mt-2 text-sm text-red-700">{error}</p>}

            {/* Room message feed */}
            <div className="mt-3 pt-2 border-t border-teal-200">
              <p className="text-xs font-semibold text-teal-900 mb-1">{t.ui.voiceChatTitle}</p>
              <div
                ref={messageFeedRef}
                className="h-[120px] overflow-y-auto space-y-1 rounded-lg bg-teal-100/40 px-2 py-1"
              >
                {roomMessages.length === 0 ? (
                  <p className="text-[11px] text-teal-600 italic pt-1">{t.ui.voiceChatEmpty}</p>
                ) : (
                  roomMessages.map((msg) => (
                    <div key={msg.id} className="text-[11px] leading-snug">
                      {msg.type === 'system' ? (
                        <div className="text-teal-800 bg-teal-100 rounded px-1.5 py-1 border border-teal-200 whitespace-pre-wrap break-words">
                          {msg.content}
                        </div>
                      ) : (
                        <div
                          className={`rounded px-1.5 py-1 border break-words ${
                            msg.senderId === myPlayerId
                              ? 'bg-teal-50 border-teal-200'
                              : 'bg-white/80 border-teal-100'
                          }`}
                        >
                          <div className="font-semibold text-teal-900">
                            {msg.senderName || t.ui.voiceUnknownParticipant}
                            {msg.senderId === myPlayerId ? ` (${t.ui.voiceParticipantYouSuffix})` : ''}
                          </div>
                          <div className="text-teal-900 whitespace-pre-wrap">{msg.content}</div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              <form
                className="mt-1 flex flex-col gap-1 sm:flex-row sm:items-center"
                onSubmit={(event) => {
                  event.preventDefault();
                  sendChatMessage();
                }}
              >
                <input
                  type="text"
                  value={chatDraft}
                  onChange={(event) => setChatDraft(event.target.value)}
                  onMouseDown={(event) => event.stopPropagation()}
                  placeholder={t.ui.voiceChatPlaceholder}
                  maxLength={500}
                  className="w-full min-w-0 flex-1 px-2 py-1.5 border border-teal-200 rounded-lg text-[11px] bg-white text-teal-900 placeholder:text-teal-500 focus:outline-none focus:border-teal-400"
                />
                <button
                  type="submit"
                  disabled={!chatDraft.trim() || !socket || !currentRoom}
                  onMouseDown={(event) => event.stopPropagation()}
                  className="w-full sm:w-auto shrink-0 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-[11px] font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t.ui.voiceChatSend}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};
